import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getMe } from "../apis/LoginApi";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const getStoredUser = () => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const storedToken =
    typeof window !== "undefined"
      ? localStorage.getItem("auth_token") || localStorage.getItem("token")
      : null;
  const initialUser = typeof window !== "undefined" ? getStoredUser() : null;

  const [user, setUser] = useState(initialUser);
  const [status, setStatus] = useState(
    initialUser || storedToken ? "authenticated" : "loading"
  ); // loading | authenticated | unauthenticated

  const loginSession = (userData, token) => {
    if (token) {
      localStorage.setItem("auth_token", token);
      localStorage.setItem("token", token);
    }
    if (userData) {
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
    }
    setStatus("authenticated");
  };

  const logoutSession = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.clear();
    setUser(null);
    setStatus("unauthenticated");
  };

  const fetchUser = useCallback(() => {
    return getMe()
      .then((res) => {
        if (res?.data?.user) {
          setUser(res.data.user);
          localStorage.setItem("user", JSON.stringify(res.data.user));
          setStatus("authenticated");
        }
      })
      .catch((err) => {
        const httpStatus = err?.response?.status;
        const localUser = getStoredUser();
        const hasToken =
          localStorage.getItem("auth_token") || localStorage.getItem("token");

        // If we have a local session stored (important for Mac/Safari where 3rd-party cookies are blocked)
        if (localUser || hasToken) {
          if (localUser) setUser(localUser);
          setStatus("authenticated");
          return;
        }

        // Only a real 401/403 means "not logged in". A network blip or
        // server error shouldn't force out a user we previously confirmed.
        if (httpStatus === 401 || httpStatus === 403) {
          setUser(null);
          setStatus("unauthenticated");
        } else {
          setStatus((prev) => (prev === "authenticated" ? "authenticated" : "unauthenticated"));
        }
      });
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const role = user?.role ? user.role.toLowerCase() : null;

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        role,
        refetch: fetchUser,
        loginSession,
        logoutSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
